import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import {
  faBuilding,
  faGear,
  faRightFromBracket,
} from '@fortawesome/pro-regular-svg-icons'
import {
  getCurrentUser,
  getFreeTrialStatus,
  getIsRequiredStepsOnboarding,
  getUserIsAdmin,
} from '@/selectors/user.selectors'
import { useSidebarConfig } from './navigation/config'
import { useReselector } from '@/utils/sharedHooks'
import { useIsMobileTakeover } from '@/utils/deviceWidthHelpers'
import { selectPanelOpen } from '@/features/adviser-chat/adviser-chat.selectors'
import { useLaunchHavenCubk } from '@/features/adviser-chat/hooks/useLaunchHavenCubk'
import { useOpenHavenFullScreen } from '@/features/adviser-chat/hooks/useOpenHavenFullScreen'
import { isHavenPath } from '@/features/adviser-chat/lib/havenRoutes'
import { HAVEN_ENTRY_POINT } from '@/features/adviser-chat/lib/types'
import { isCompliancePath } from '@/features/compliance/lib/constants'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useAppDispatch } from '@/utils/typeHelpers'
import { logoutUser } from '@/actions/authActions'
import { useAnalyticsReset } from '@/features/amplitude'
import { isAdminContext } from '@/utils/adminContext'
import { performAdminLogout } from '@/utils/adminSession'

import { layoutStyles, mainContentStyles } from './layoutStyles'
import { mtc } from '@/styles'
import { Sidebar } from '@/ui'
import {
  FETCH_USER_PERMISSIONS_KEY,
  getUserPermissions,
} from '@/actions/permissionActions'
import { useRouteMapper } from './useRouteMapper'
import { selectUserHasPermissionsForRoute } from '@/reducers/auth/permission.selectors'
import { getIsFetching } from '@/reducers/fetch'
import { useRouteConfig } from '@/utils/routeHelpers'
import { RouteAccessDenied } from '@/components/routes/RouteAccessDenied'
import { clearOpenFeatureTargetingKey, FEATURE_FLAG_KEYS } from '@/flags'
import { clearUnitLocalStorage } from '@/features/banking/banking.constants'
import { clearPlaidConnectReminderSuppression } from '@/features/banking/plaid-connect-reminder'
import { Flyout } from '@/components/Flyout'
import { AdviserWidget } from '@/components/adviser-chat'
import FreeTrialBanner from '@/features/user/signup/FreeTrialBanner'
import { TrialStatus } from '@/reducers/auth/userReducer'
import { BaseRouterBanners } from '@/components/platform-banners/BaseRouterBanners'
import { useSidebarNavAnnouncement } from '@/features/user/dashboard/sidebar-nav-announcement/useSidebarNavAnnouncement'

interface LayoutProps {
  children?: ReactNode
  useOldDesignSystem?: boolean
}

// Used for DS2 Layouts
export const Layout = ({ children, useOldDesignSystem }: LayoutProps) => {
  const user = useReselector(getCurrentUser)
  // Used to render wide vs non wide layouts
  const isAdmin = useReselector(getUserIsAdmin)
  const isRequiredStepsOnboarding = useReselector(getIsRequiredStepsOnboarding)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const resetAnalytics = useAnalyticsReset()
  const isMobileTakeover = useIsMobileTakeover()
  const rawCategories = useSidebarConfig()
  const lastCategoriesRef = useRef(rawCategories)
  if (rawCategories !== null) lastCategoriesRef.current = rawCategories
  const categories = lastCategoriesRef.current

  // The practice-readiness funnel runs pre-conversion, so the app sidebar has
  // nothing to offer it: every destination either dead-ends or pulls a prospect
  // out of the funnel before they have an account. These screens drop the
  // sidebar entirely and keep the rest of the shell (banners, permissions,
  // flyout). Branched here rather than split into a funnel-specific layout —
  // the funnel is already route-identified, and a parallel layout would have to
  // re-derive everything below it.
  const isComplianceFlow = isCompliancePath(location.pathname)
  const panelOpen = useReselector(selectPanelOpen)
  const sidebarNavAnnouncement = useSidebarNavAnnouncement()
  const havenCubkEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableHavenCubk,
    false
  )
  const isOnboarding = Boolean(user) && isNil(user?.onboardingItemsCompletedAt)
  const launchHavenCubk = useLaunchHavenCubk(HAVEN_ENTRY_POINT.sidebarAskHaven)
  const openHavenFullScreen = useOpenHavenFullScreen()
  const isHavenFullScreen = isHavenPath(location.pathname)
  const handleAskHaven = useCallback(() => {
    if (havenCubkEnabled && isOnboarding) {
      launchHavenCubk()
      return
    }
    openHavenFullScreen()
  }, [havenCubkEnabled, isOnboarding, launchHavenCubk, openHavenFullScreen])
  const { ALL_ROUTES } = useRouteMapper()
  const currentRoute = useRouteConfig(ALL_ROUTES)
  const trialStatus = useReselector(getFreeTrialStatus)

  const userHasPermissionToViewRoute = useReselector(
    selectUserHasPermissionsForRoute,
    ALL_ROUTES,
    currentRoute.key
  )
  const isFetchingPermissions = useReselector(
    getIsFetching,
    FETCH_USER_PERMISSIONS_KEY
  )
  const routeAccessDeniedModalOpen = useMemo(
    () => !userHasPermissionToViewRoute && !isFetchingPermissions,
    [userHasPermissionToViewRoute, isFetchingPermissions]
  )

  useEffect(() => {
    if (!isAdmin) {
      dispatch(getUserPermissions())
    }
  }, [dispatch, isAdmin])

  const handleLogoutClick = useCallback(async () => {
    if (isAdminContext()) {
      await performAdminLogout()
      return
    }
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    await clearOpenFeatureTargetingKey()
    clearUnitLocalStorage()
    clearPlaidConnectReminderSuppression()
    navigate('/login')
  }, [dispatch, navigate, resetAnalytics])

  const practiceProfileOption = {
    icon: faBuilding,
    onClick: () => navigate('/practice/profile'),
    text: 'Practice Profile',
  }
  const dropdownOptions = isAdmin
    ? [
        {
          icon: faRightFromBracket,
          onClick: handleLogoutClick,
          text: 'Logout',
        },
      ]
    : [
        ...(isRequiredStepsOnboarding ? [] : [practiceProfileOption]),
        {
          icon: faGear,
          onClick: () => navigate('/accounts'),
          text: 'User Settings',
        },
        {
          icon: faRightFromBracket,
          onClick: handleLogoutClick,
          text: 'Logout',
        },
      ]
  return (
    <div data-app-layout-root className={layoutStyles()}>
      {!isComplianceFlow && (
        <div className="contents" data-print-exclude>
          <Sidebar
            device={'desktop'}
            dropdownOptions={dropdownOptions}
            categories={categories}
            user={user || null}
            footerBanner={
              trialStatus === TrialStatus.active && <FreeTrialBanner />
            }
            navAnnouncement={sidebarNavAnnouncement}
            forceCollapse={panelOpen || isHavenFullScreen}
            onAskHaven={handleAskHaven}
          />
        </div>
      )}

      <main
        data-app-layout-main
        className={mtc(
          mainContentStyles(),
          isHavenFullScreen && 'hidden',
          // Keeps its width when the sidebar takes over the screen, so the
          // content is pushed fully off-screen instead of squeezed into a sliver.
          isMobileTakeover &&
            !isComplianceFlow &&
            'w-[calc(100vw-68px)] flex-none',
          // The rounded left edge and left border only make sense butted up
          // against the sidebar; with no sidebar they leave a notch at the
          // viewport edge. The funnel also sits tighter to the top than the
          // product does — every breakpoint, since the shell steps its own
          // padding up to `py-10` and a bare `py-6` would not reach those.
          isComplianceFlow && 'rounded-l-none border-l-0 py-6 sm:py-6 md:py-6',
          useOldDesignSystem && 'semantic-ui-root'
        )}
        style={{
          boxShadow: '0 1px 2px rgba(18, 55, 105, 0.078)',
        }}
      >
        <div
          data-app-layout-content
          className={mtc(
            'w-full mx-auto flex flex-col gap-4',
            !isAdmin && 'max-w-[1200px]'
          )}
        >
          {routeAccessDeniedModalOpen ? (
            <RouteAccessDenied
              customComponent={
                currentRoute?.config?.requiredPermissions
                  ?.customPermissionComponent
              }
            />
          ) : (
            <>
              <div className="contents" data-print-exclude>
                <BaseRouterBanners />
              </div>
              {children}
            </>
          )}
        </div>
        <Flyout />
      </main>
      {/* AdviserWidget sits as a flex sibling so the panel pushes main content left */}
      <div className="contents" data-print-exclude>
        <AdviserWidget />
      </div>
    </div>
  )
}
